<template>
    <div id="financing-page" class="financing-pc">
        <div class="resou-title"><span>企业融资 》融资项目</span></div>

        <div class="finance-toolbar">
            <div class="finance-view-tabs">
                <button
                    v-for="item in viewOptions"
                    :key="item.value"
                    type="button"
                    :class="{ active: currentView === item.value }"
                    @click="switchView(item.value)"
                >{{ item.label }}</button>
            </div>
            <div class="finance-links">
                <el-button type="text" icon="el-icon-office-building" @click="openOrganizations">投资机构</el-button>
                <span class="finance-link-divider"></span>
                <el-button type="text" icon="el-icon-user" @click="openMembers">企业会员</el-button>
            </div>
        </div>

        <div class="finance-query-bar">
            <el-form :inline="true" size="mini" @submit.native.prevent>
                <el-form-item :label="currentView === 'activity' ? '活动名称:' : '项目名称:'">
                    <el-input
                        v-model.trim="keyword"
                        clearable
                        :placeholder="currentView === 'activity' ? '请输入活动名称' : '请输入项目名称'"
                        @keyup.enter.native="fetchCurrentList"
                    ></el-input>
                </el-form-item>
                <el-form-item>
                    <el-button type="primary" icon="el-icon-search" @click="fetchCurrentList">查询</el-button>
                    <el-button icon="el-icon-refresh" @click="resetQuery">重置</el-button>
                    <el-button type="primary" icon="el-icon-plus" @click="openPublish">发布</el-button>
                </el-form-item>
            </el-form>
        </div>

        <div v-loading="loading" class="finance-list-wrap">
            <div v-if="currentView === 'activity'" class="finance-activity-list">
                <div v-for="item in activityList" :key="item.id" class="finance-activity-row" @click="openDetail(item)">
                    <div class="finance-activity-cover">
                        <img v-if="item.cover" :src="item.cover" alt="活动封面">
                        <div v-else class="finance-cover-fallback">
                            <span>{{ item.bannerDate || '投融资活动' }}</span>
                            <strong>{{ item.title || '活动详情' }}</strong>
                            <small>趋势洞察 · 资源链接 · 投资机会</small>
                        </div>
                    </div>
                    <div class="finance-row-main">
                        <div class="finance-row-title">
                            <h3>{{ item.title || '--' }}</h3>
                            <el-tag size="mini" type="warning">报名中</el-tag>
                            <span v-if="isAuditAdmin && auditStatusText(item.audit_status)" class="finance-audit-status" :class="auditStatusClass(item.audit_status)">{{ auditStatusText(item.audit_status) }}</span>
                        </div>
                        <p class="finance-row-description">{{ item.description || '暂无活动介绍' }}</p>
                        <div class="finance-row-meta">
                            <span><i class="el-icon-date"></i> 活动时间：{{ item.time || '待定' }}</span>
                            <span><i class="el-icon-location-outline"></i> 活动地点：{{ item.location || '待定' }}</span>
                            <span><i class="el-icon-connection"></i> 活动形式：{{ item.activityForm || '待定' }}</span>
                        </div>
                    </div>
                    <div class="finance-row-side">
                        <strong>{{ item.fee }}</strong>
                        <span>{{ item.participants || '参与人数待定' }}</span>
                        <div class="finance-row-actions">
                            <el-button v-if="isAuditAdmin" type="primary" plain size="mini" @click.stop="openAuditDialog(item)">审核</el-button>
                            <el-button v-if="isAuditAdmin" type="danger" plain size="mini" @click.stop="openDeleteDialog(item)">删除</el-button>
                            <el-button type="primary" size="mini" @click.stop="openDetail(item)">查看详情</el-button>
                        </div>
                    </div>
                </div>
                <el-empty v-if="!loading && !activityList.length" description="暂无投融资活动"></el-empty>
            </div>

            <div v-else class="finance-project-list">
                <div v-for="item in visibleProjectList" :key="item.id" class="finance-project-row" @click="openDetail(item)">
                    <div class="finance-project-cover">
                        <img v-if="item.cover" :src="item.cover" alt="项目封面">
                        <div v-else class="finance-project-cover-fallback"><i class="el-icon-s-finance"></i></div>
                    </div>
                    <div class="finance-row-main">
                        <div class="finance-row-title">
                            <h3>{{ item.title || '--' }}</h3>
                            <el-tag size="mini" :type="projectTagType(item.type)">{{ viewLabel(item.type) }}</el-tag>
                            <span v-if="isAuditAdmin && auditStatusText(item.audit_status)" class="finance-audit-status" :class="auditStatusClass(item.audit_status)">{{ auditStatusText(item.audit_status) }}</span>
                        </div>
                        <p class="finance-row-description">{{ item.description || '暂无项目介绍' }}</p>
                        <div class="finance-row-meta">
                            <span><i class="el-icon-time"></i> 发布时间：{{ item.publishTime || '--' }}</span>
                            <span><i class="el-icon-user"></i> 发布人：{{ item.publisher || '--' }}</span>
                            <span><i class="el-icon-office-building"></i> 企业：{{ item.enterpriseName || '--' }}</span>
                        </div>
                        <div v-if="item.type === 'finance'" class="finance-subscription-unit">
                            <span>认购单位：{{ item.subscriptionUnit || '暂无' }}</span>
                        </div>
                        <div v-if="item.type === 'crowdfunding'" class="finance-project-quotes">
                            <span>当前低价：<strong>{{ item.currentQuote || '暂无报价' }}</strong></span>
                            <span>我的报价：{{ item.myQuoted ? item.myQuote : '未报价' }}</span>
                            <span>中标单位：{{ item.winEnterpriseName || '暂未确认' }}</span>
                        </div>
                    </div>
                    <div class="finance-row-side">
                        <strong>{{ item.type === 'crowdfunding' ? item.currentQuote : item.amount }}</strong>
                        <span>{{ item.type === 'crowdfunding' ? `${item.quoteCount || 0} 个报价` : `${item.followCount} 人关注 · ${item.investorCount} 人意向` }}</span>
                        <div class="finance-row-actions">
                            <el-button v-if="isAuditAdmin" type="primary" plain size="mini" @click.stop="openAuditDialog(item)">审核</el-button>
                            <el-button v-if="isAuditAdmin" type="danger" plain size="mini" @click.stop="openDeleteDialog(item)">删除</el-button>
                            <el-button v-if="isAuditAdmin && item.type === 'finance'" type="warning" plain size="mini" @click.stop="openEditDialog(item)">编辑</el-button>
                            <el-button type="primary" size="mini" @click.stop="handleProjectAction(item)">{{ item.actionText }}</el-button>
                        </div>
                    </div>
                </div>
                <el-empty v-if="!loading && !visibleProjectList.length" :description="'暂无'+viewLabel(currentView)"></el-empty>
            </div>
        </div>

        <div class="cr_footer">
            <div class="footer_left"><i class="el-icon-s-finance"></i> 共加载 {{ currentListLength }} 条{{ viewLabel(currentView) }}</div>
        </div>

        <el-dialog
            v-dialogDrag
            :title="detailTitle"
            :visible.sync="detailVisible"
            width="760px"
            top="5vh"
            custom-class="finance-detail-dialog"
            :modal-append-to-body="true"
            :append-to-body="true"
        >
            <div v-loading="detailLoading" class="finance-detail-body">
                <div v-if="detail.cover" class="finance-detail-cover"><img :src="detail.cover" alt="封面"></div>
                <div class="finance-detail-title-row">
                    <h3>{{ detail.title || '--' }}</h3>
                    <el-tag size="small" :type="detail.type === 'activity' ? 'warning' : projectTagType(detail.type)">{{ viewLabel(detail.type) }}</el-tag>
                </div>
                <p class="finance-detail-description">{{ detail.description || '暂无介绍' }}</p>

                <template v-if="detail.type === 'activity'">
                    <div class="finance-detail-grid">
                        <div><span>活动时间</span><strong>{{ detail.time || '待定' }}</strong></div>
                        <div><span>活动地点</span><strong>{{ detail.location || '待定' }}</strong></div>
                        <div><span>活动形式</span><strong>{{ detail.activityForm || '待定' }}</strong></div>
                        <div><span>参与人员</span><strong>{{ detail.participants || '待定' }}</strong></div>
                        <div><span>费用</span><strong>{{ detail.fee || '免费' }}</strong></div>
                        <div><span>主办企业</span><strong>{{ detail.enterpriseName || '--' }}</strong></div>
                    </div>
                </template>
                <template v-else>
                    <div class="finance-project-amount"><span>{{ amountLabel(detail.type) }}</span><strong>{{ detail.amount || '面议' }}</strong></div>
                    <div class="finance-detail-grid">
                        <div><span>发布人</span><strong>{{ detail.publisher || '--' }}</strong></div>
                        <div><span>所属企业</span><strong>{{ detail.enterpriseName || '--' }}</strong></div>
                        <div><span>发布时间</span><strong>{{ detail.publishTime || '--' }}</strong></div>
                        <div><span>关注人数</span><strong>{{ detail.followCount || 0 }} 人</strong></div>
                        <div v-if="detail.type === 'finance'"><span>认购单位</span><strong>{{ detail.subscriptionUnit || '暂无' }}</strong></div>
                        <template v-if="detail.type === 'crowdfunding'">
                            <div><span>当前低价</span><strong class="finance-quote-value">{{ detail.currentQuote || '暂无报价' }}</strong></div>
                            <div><span>我的报价</span><strong>{{ detail.myQuoted ? detail.myQuote : '未报价' }}</strong></div>
                            <div><span>报价数量</span><strong>{{ detail.quoteCount || 0 }} 个</strong></div>
                            <div><span>中标单位</span><strong>{{ detail.winEnterpriseName || '暂未确认' }}</strong></div>
                        </template>
                    </div>
                    <el-button v-if="detail.projectBp" type="text" icon="el-icon-document" @click="openProjectBp">查看项目 BP</el-button>
                </template>
            </div>
            <span slot="footer">
                <el-button :loading="actionLoading" icon="el-icon-star-off" @click="toggleDetailFollow">{{ detail.followed ? '取消关注' : '关注' }}</el-button>
                <el-button v-if="detail.type === 'activity'" type="primary" :loading="actionLoading" @click="toggleActivitySignup">{{ detail.signedUp ? '取消报名' : '活动报名' }}</el-button>
                <el-button v-else type="primary" :loading="actionLoading" @click="handleInvest">{{ detail.type === 'crowdfunding' ? '报价' : (detail.invested ? '取消投资意向' : detail.actionText) }}</el-button>
            </span>
        </el-dialog>

        <el-dialog
            v-dialogDrag
            :title="auditTarget.type === 'member' ? '企业会员审核' : viewLabel(auditTarget.type) + '审核'"
            :visible.sync="auditVisible"
            width="520px"
            custom-class="finance-audit-dialog"
            :modal-append-to-body="true"
            :append-to-body="true"
            @closed="resetAuditForm"
        >
            <el-form ref="auditForm" :model="auditForm" :rules="auditRules" label-width="90px">
                <el-form-item label="审核对象"><el-input :value="auditTarget.title || '--'" disabled></el-input></el-form-item>
                <el-form-item label="审核结果" prop="status">
                    <el-radio-group v-model="auditForm.status"><el-radio :label="1">通过</el-radio><el-radio :label="2">不通过</el-radio></el-radio-group>
                </el-form-item>
                <el-form-item label="审核备注"><el-input v-model="auditForm.remark" type="textarea" :rows="4" maxlength="500" show-word-limit placeholder="请输入审核备注"></el-input></el-form-item>
            </el-form>
            <span slot="footer"><el-button @click="auditVisible=false">取消</el-button><el-button type="primary" :loading="auditSubmitting" @click="submitAudit">确认审核</el-button></span>
        </el-dialog>

        <el-dialog
            v-dialogDrag
            title="删除确认"
            :visible.sync="deleteVisible"
            width="420px"
            custom-class="finance-delete-dialog"
            :modal-append-to-body="true"
            :append-to-body="true"
            @closed="resetDeleteForm"
        >
            <div class="finance-delete-confirm">
                <i class="el-icon-warning-outline"></i>
                <span>确定删除“{{ deleteTarget.title || '--' }}”吗？删除后不可恢复。</span>
            </div>
            <span slot="footer"><el-button @click="deleteVisible=false">取消</el-button><el-button type="danger" :loading="deleteSubmitting" @click="submitDelete">确认删除</el-button></span>
        </el-dialog>

        <el-dialog
            v-dialogDrag
            :title="detail.type === 'crowdfunding' ? '接包报价' : '提交投资意向'"
            :visible.sync="investVisible"
            width="520px"
            custom-class="finance-invest-dialog"
            :modal-append-to-body="true"
            :append-to-body="true"
            @closed="resetInvestForm"
        >
            <el-form ref="investForm" :model="investForm" :rules="investRules" label-width="100px" size="small">
                <el-form-item :label="detail.type === 'crowdfunding' ? '外包项目' : '投资项目'"><el-input :value="detail.title || '--'" disabled></el-input></el-form-item>
                <el-form-item :label="detail.type === 'crowdfunding' ? '我的报价' : '投资金额'" prop="amount"><el-input v-model.trim="investForm.amount" maxlength="14" inputmode="decimal" :placeholder="detail.type === 'crowdfunding' ? '请输入报价金额' : '请输入投资金额'"><template slot="append">万元</template></el-input></el-form-item>
                <el-form-item label="联系人" prop="contactName"><el-input v-model.trim="investForm.contactName" maxlength="30"></el-input></el-form-item>
                <el-form-item label="联系电话" prop="contactPhone"><el-input v-model.trim="investForm.contactPhone" maxlength="20"></el-input></el-form-item>
            </el-form>
            <span slot="footer"><el-button @click="investVisible=false">取消</el-button><el-button type="primary" :loading="actionLoading" @click="submitInvest">{{ detail.type === 'crowdfunding' ? '确认报价' : '确认投资' }}</el-button></span>
        </el-dialog>

        <el-dialog
            v-dialogDrag
            :title="publishEditing ? '编辑融资项目' : (publishForm.type === 'activity' ? '发布投融资活动' : '发布融资项目')"
            :visible.sync="publishVisible"
            width="900px"
            top="4vh"
            custom-class="finance-publish-dialog"
            :modal-append-to-body="true"
            :append-to-body="true"
            @closed="resetPublishForm"
        >
            <div v-loading="publishLoading" class="finance-publish-body">
                <el-form ref="publishForm" class="finance-publish-form" :model="publishForm" :rules="publishRules" label-width="100px" size="small">
                    <el-row :gutter="20" class="finance-publish-row">
                        <el-col :span="12">
                            <el-form-item label="发布类型" prop="type">
                                <el-select v-model="publishForm.type" :disabled="publishEditing" style="width:100%;"><el-option v-for="item in viewOptions" :key="item.value" :label="item.label" :value="item.value"></el-option></el-select>
                            </el-form-item>
                        </el-col>
                        <el-col :span="12">
                            <el-form-item :label="publishForm.type === 'activity' ? '活动名称' : '项目名称'" prop="name"><el-input v-model.trim="publishForm.name" maxlength="50"></el-input></el-form-item>
                        </el-col>
                    </el-row>
                    <template v-if="publishForm.type === 'activity'">
                        <el-form-item label="活动时间" prop="timeRange"><el-date-picker v-model="publishForm.timeRange" type="datetimerange" range-separator="至" start-placeholder="开始时间" end-placeholder="结束时间" style="width:100%;"></el-date-picker></el-form-item>
                        <el-row :gutter="18">
                            <el-col :span="12"><el-form-item label="活动地点" prop="location"><el-input v-model.trim="publishForm.location" maxlength="100"></el-input></el-form-item></el-col>
                            <el-col :span="12"><el-form-item label="活动形式"><el-input v-model.trim="publishForm.activityForm" maxlength="50" placeholder="如：线下路演"></el-input></el-form-item></el-col>
                        </el-row>
                        <el-row :gutter="18">
                            <el-col :span="12"><el-form-item label="参与人员"><el-input v-model.trim="publishForm.participants" maxlength="30" placeholder="如：50人"></el-input></el-form-item></el-col>
                            <el-col :span="12"><el-form-item label="费用类型"><el-select v-model="publishForm.feeType" style="width:100%;"><el-option label="免费" value="免费"></el-option><el-option label="金额" value="金额"></el-option><el-option label="面议" value="面议"></el-option></el-select></el-form-item></el-col>
                        </el-row>
                        <el-form-item v-if="publishForm.feeType === '金额'" label="费用金额"><el-input-number v-model="publishForm.feeAmount" :min="0" :precision="2" controls-position="right" style="width:100%;"></el-input-number></el-form-item>
                    </template>
                    <template v-else>
                        <el-row :gutter="20" class="finance-publish-row">
                            <el-col :span="12">
                                <el-form-item :label="amountLabel(publishForm.type)" prop="financingAmount"><el-input v-model.trim="publishForm.financingAmount" maxlength="40" placeholder="如：500万元"></el-input></el-form-item>
                            </el-col>
                            <el-col :span="12">
                                <el-form-item label="项目 BP">
                                    <el-upload action="#" :show-file-list="false" :http-request="requestBpUpload" accept=".pdf,.doc,.docx,.ppt,.pptx">
                                        <el-button size="small" icon="el-icon-upload2" :loading="bpUploading">{{ publishForm.bpName || '选择文件' }}</el-button>
                                    </el-upload>
                                </el-form-item>
                            </el-col>
                        </el-row>
                    </template>
                    <el-form-item :label="publishForm.type === 'activity' ? '活动封面' : '项目封面'">
                        <el-upload action="#" accept="image/*" :show-file-list="false" :auto-upload="false" :on-change="handleCoverChange">
                            <div class="finance-cover-upload" v-loading="coverUploading">
                                <img v-if="publishForm.coverPreview" :src="publishForm.coverPreview" alt="封面">
                                <div v-else><i class="el-icon-camera"></i><span>上传封面</span></div>
                            </div>
                        </el-upload>
                    </el-form-item>
                    <el-form-item :label="publishForm.type === 'activity' ? '活动介绍' : '项目介绍'"><el-input v-model="publishForm.description" type="textarea" :rows="5" maxlength="500" show-word-limit></el-input></el-form-item>
                </el-form>
            </div>
            <span slot="footer"><el-button @click="publishVisible=false">取消</el-button><el-button type="primary" :loading="publishSaving" @click="submitPublish">{{ publishEditing ? '保存' : '发布' }}</el-button></span>
        </el-dialog>

        <el-dialog
            v-dialogDrag
            title="裁切融资封面"
            :visible.sync="cropperModel.val"
            width="1100px"
            top="5vh"
            custom-class="finance-cropper-dialog"
            :modal-append-to-body="true"
            :append-to-body="true"
            :close-on-click-modal="false"
            v-if="cropperModel.val"
            @closed="resetCropper"
        >
            <v-vuecropper
                :key="cropperKey"
                :cropperModel="cropperModel"
                :imgFile="cropperFile"
                :imgW="490"
                :imgH="180"
                :fixedNumber="[109,40]"
                :fixed="true"
                :outputW="654"
                :outputH="240"
                outputType="jpg"
                v-on:upload="handleCroppedCover"
            ></v-vuecropper>
        </el-dialog>

        <el-dialog
            v-dialogDrag
            title="投资机构"
            :visible.sync="organizationsVisible"
            width="760px"
            top="5vh"
            custom-class="finance-directory-dialog"
            :modal-append-to-body="true"
            :append-to-body="true"
        >
            <div class="finance-directory-toolbar"><el-input v-model.trim="organizationKeyword" clearable size="small" placeholder="搜索投资机构" @keyup.enter.native="fetchOrganizations"><el-button slot="append" icon="el-icon-search" @click="fetchOrganizations"></el-button></el-input></div>
            <el-table v-loading="directoryLoading" :data="organizationList" border stripe height="430" empty-text="暂无投资机构">
                <el-table-column label="机构" min-width="220"><template slot-scope="scope"><div class="finance-directory-name"><div class="finance-directory-logo"><img v-if="scope.row.icon" :src="scope.row.icon" alt=""><span v-else>{{ scope.row.name.slice(0,1) }}</span></div><strong>{{ scope.row.name }}</strong></div></template></el-table-column>
                <el-table-column prop="type" label="机构类型" min-width="120"></el-table-column>
                <el-table-column prop="leader" label="负责人" width="110"></el-table-column>
                <el-table-column prop="investedEnterprises" label="已投资企业" min-width="180" show-overflow-tooltip></el-table-column>
                <el-table-column label="操作" width="100" align="center"><template slot-scope="scope"><el-button type="text" @click="toggleOrganizationFollow(scope.row)">{{ scope.row.followed ? '取消关注' : '关注' }}</el-button></template></el-table-column>
            </el-table>
        </el-dialog>

        <el-dialog
            v-dialogDrag
            title="企业会员"
            :visible.sync="membersVisible"
            width="900px"
            top="5vh"
            custom-class="finance-directory-dialog"
            :modal-append-to-body="true"
            :append-to-body="true"
        >
            <div class="finance-directory-toolbar"><el-input v-model.trim="memberKeyword" clearable size="small" placeholder="搜索企业会员" @keyup.enter.native="fetchMembers"><el-button slot="append" icon="el-icon-search" @click="fetchMembers"></el-button></el-input></div>
            <el-table v-loading="directoryLoading" :data="memberList" border stripe height="500" empty-text="暂无企业会员">
                <el-table-column prop="name" label="企业" min-width="260" show-overflow-tooltip></el-table-column>
                <el-table-column prop="summary" label="企业信息" min-width="180" show-overflow-tooltip></el-table-column>
                <el-table-column prop="corporateName" label="负责人" width="110"></el-table-column>
                <el-table-column prop="contactPhone" label="联系电话" width="150" show-overflow-tooltip></el-table-column>
                <el-table-column v-if="isAuditAdmin" label="审核状态" width="105" align="center"><template slot-scope="scope"><span :class="auditStatusClass(scope.row.audit_status)">{{ auditStatusText(scope.row.audit_status) || '待审核' }}</span></template></el-table-column>
                <el-table-column label="操作" :width="isAuditAdmin ? 220 : 100" align="center"><template slot-scope="scope"><el-button type="text" @click="toggleMemberFollow(scope.row)">{{ scope.row.followed ? '取消关注' : '关注' }}</el-button><el-button v-if="isAuditAdmin" type="text" @click="openAuditDialog(scope.row)">审核</el-button><el-button v-if="canManageMembers" type="text" class="finance-member-delete" @click="openDeleteDialog(scope.row)">删除</el-button></template></el-table-column>
            </el-table>
            <span slot="footer"><el-button type="primary" :loading="memberApplying" @click="applyMember">申请加入</el-button></span>
        </el-dialog>
    </div>
</template>

<script>
import vuecropper from "../commonComponents/vueCropper.vue";

const VIEW_OPTIONS = [
    { label:'投融资活动',value:'activity' },
    { label:'融资项目',value:'finance' },
    { label:'外包项目',value:'crowdfunding' },
    { label:'出售项目',value:'sale' }
];

const TYPE_LABELS = { activity:'投融资活动',finance:'融资项目',crowdfunding:'外包项目',sale:'出售项目' };
const API_TYPE_LABELS = { finance:'融资项目',crowdfunding:'外包项目',sale:'出售项目' };

function createPublishForm(type='activity') {
    return {
        type,name:'',description:'',timeRange:[],location:'',activityForm:'',participants:'',
        feeType:'免费',feeAmount:0,financingAmount:'',coverImage:'',coverPreview:'',projectBp:'',bpName:''
    };
}

export default {
    name:'EnterpriseFinancing',
    components:{
        "v-vuecropper": vuecropper
    },
    data() {
        return {
            viewOptions:VIEW_OPTIONS,
            currentView:'activity',
            keyword:'',
            loading:false,
            activityList:[],
            projectList:[],
            detailVisible:false,
            detailLoading:false,
            detail:{},
            auditVisible:false,
            auditSubmitting:false,
            auditTarget:{},
            auditForm:{ status:1,remark:'' },
            auditRules:{ status:[{ required:true,message:'请选择审核结果',trigger:'change' }] },
            deleteVisible:false,
            deleteSubmitting:false,
            deleteTarget:{},
            actionLoading:false,
            investVisible:false,
            investForm:{ amount:'',contactName:'',contactPhone:'' },
            investRules:{
                amount:[{ required:true,validator:(rule,value,callback)=>this.validateInvestAmount(rule,value,callback),trigger:['blur','change'] }],
                contactName:[{ required:true,message:'请输入联系人',trigger:'blur' }],
                contactPhone:[{ required:true,message:'请输入联系电话',trigger:'blur' }]
            },
            publishVisible:false,
            publishEditing:false,
            publishEditId:'',
            publishLoading:false,
            publishSaving:false,
            coverUploading:false,
            bpUploading:false,
            cropperModel:{ val:false },
            cropperFile:[],
            cropperKey:0,
            publishForm:createPublishForm(),
            publishRules:{
                type:[{ required:true,message:'请选择发布类型',trigger:'change' }],
                name:[{ required:true,message:'请输入名称',trigger:'blur' }],
                timeRange:[{ type:'array',required:true,message:'请选择活动时间',trigger:'change' }],
                location:[{ required:true,message:'请输入活动地点',trigger:'blur' }],
                financingAmount:[{ required:true,message:'请输入融资金额',trigger:'blur' }]
            },
            organizationsVisible:false,
            membersVisible:false,
            directoryLoading:false,
            organizationKeyword:'',
            memberKeyword:'',
            organizationList:[],
            memberList:[],
            memberApplying:false
        };
    },
    computed:{
        visibleProjectList() { return this.projectList.filter(item=>item.type===this.currentView); },
        currentListLength() { return this.currentView==='activity' ? this.activityList.length : this.visibleProjectList.length; },
        detailTitle() { return this.detail.type==='activity' ? '投融资活动详情' : '融资项目详情'; },
        isAuditAdmin() {
            const user=this.getStoredUser();
            return String(localStorage.getItem('acctype') || user.acctype || '')==='3';
        },
        canManageMembers() {
            const user=this.getStoredUser();
            return [localStorage.getItem('acctype'),localStorage.getItem('user_type'),localStorage.getItem('usertype'),user.acctype,user.user_type,user.usertype].some(value=>String(value || '')==='3');
        }
    },
    mounted() { this.fetchCurrentList(); },
    methods:{
        getStoredUser() {
            try { return JSON.parse(localStorage.getItem('useritem') || '{}') || {}; } catch (error) { return {}; }
        },
        getUserId() {
            const user=this.getStoredUser();
            return String(user.id || user.user_id || localStorage.getItem('id') || localStorage.getItem('userid') || '').trim();
        },
        getEnterpriseInfo() {
            const user=this.getStoredUser();
            const enterprise=user.enterprise && typeof user.enterprise==='object' ? user.enterprise : {};
            return {
                id:String(localStorage.getItem('enterprise_id') || enterprise.enterprise_id || enterprise.id || user.enterprise_id || '').trim(),
                name:String(enterprise.enterprise_name || enterprise.name || user.enterprise_name || '').trim()
            };
        },
        canEditProject(item) {
            return Boolean(item && item.type === 'finance' && this.isAuditAdmin);
        },
        validateInvestAmount(rule,value,callback) {
            const amount=String(value == null ? '' : value).trim();
            const isOutsourcing=this.detail.type==='crowdfunding';
            if (!amount) return callback(new Error(isOutsourcing ? '请输入我的报价' : '请输入投资金额'));
            if (!/^\d+(\.\d{1,2})?$/.test(amount) || Number(amount)<=0) {
                return callback(new Error(isOutsourcing ? '请输入正确的报价金额' : '请输入正确的投资金额'));
            }
            callback();
        },
        unwrap(response) {
            const body=response && response.data ? response.data : response;
            const payload=body && body.data!==undefined ? body.data : body;
            return Array.isArray(payload) ? payload : (payload && Array.isArray(payload.data) ? payload.data : []);
        },
        imageUrl(path) {
            const value=String(path || '').trim();
            if (!value) return '';
            if (/^(https?:|data:|blob:)/i.test(value)) return value.replace(/^http:\/\//i,'https://');
            if (value.indexOf('/api/')===0) return value;
            if (value.indexOf('/public/')===0) return '/api/common/files'+value;
            if (value.indexOf('/')===0) return '/api/common/files'+value;
            return '/api/common/files/'+value;
        },
        stripHtml(value) { return String(value || '').replace(/<[^>]+>/g,'').replace(/&nbsp;/g,' ').replace(/\s+/g,' ').trim(); },
        formatDate(value) {
            if (!value) return '';
            const source=String(value).replace('T',' ');
            const date=new Date(source);
            if (Number.isNaN(date.getTime())) return source.slice(0,16);
            const pad=num=>String(num).padStart(2,'0');
            return date.getFullYear()+'-'+pad(date.getMonth()+1)+'-'+pad(date.getDate())+' '+pad(date.getHours())+':'+pad(date.getMinutes());
        },
        formatTimeRange(start,end) {
            const startText=this.formatDate(start);
            const endText=this.formatDate(end);
            return startText && endText ? startText+' - '+endText : startText || endText;
        },
        formatBannerDate(start,end) {
            const format=value=>{
                const match=String(value || '').match(/\d{4}-(\d{1,2})-(\d{1,2})/);
                return match ? Number(match[1])+'月'+Number(match[2])+'日' : '';
            };
            const startText=format(start);
            const endText=format(end);
            return startText && endText && startText!==endText ? startText+'-'+endText : startText || endText;
        },
        formatNumber(value) {
            const number=Number(value || 0);
            if (!Number.isFinite(number)) return '0';
            return number.toFixed(number % 1===0 ? 0 : 2).replace(/\.00$/,'').replace(/(\.\d)0$/,'$1');
        },
        formatAmount(value) {
            const raw=String(value == null ? '' : value).trim();
            if (raw && !/^[-+]?\d+(\.\d+)?$/.test(raw)) return raw;
            const number=Number(value || 0);
            if (!Number.isFinite(number) || number<=0) return '面议';
            return number>=10000 ? this.formatNumber(number/10000)+'万元' : this.formatNumber(number)+'元';
        },
        formatFee(item) {
            const type=String(item.fee_type || '').trim();
            const amount=Number(item.fee_amount || 0);
            if (type==='免费') return '免费';
            if (amount>0) return this.formatNumber(amount)+'元/人';
            return type || '面议';
        },
        auditStatusFilter() { return this.isAuditAdmin ? [0,1,2] : [1]; },
        auditStatusText(value) {
            const status=Number(value);
            if (status===0) return '待审核';
            if (status===1) return '审核通过';
            if (status===2) return '审核不通过';
            return '';
        },
        auditStatusClass(value) {
            const status=Number(value);
            if (status===0) return 'finance-audit-pending';
            if (status===1) return 'finance-audit-approved';
            if (status===2) return 'finance-audit-rejected';
            return '';
        },
        viewLabel(type) { return TYPE_LABELS[type] || '融资项目'; },
        amountLabel(type) {
            if (type==='crowdfunding') return '当前报价';
            if (type==='sale') return '出售价格';
            return '融资金额';
        },
        projectTagType(type) { return type==='sale' ? 'danger' : type==='crowdfunding' ? 'success' : ''; },
        normalizeSubscriptionUnit(item) {
            const directList=Array.isArray(item.subscription_units) ? item.subscription_units : [];
            const direct=[
                item.subscription_unit,item.subscriptionUnit,item.subscribe_unit,item.subscribeUnit,
                item.subscription_enterprise_name,item.subscriptionEnterpriseName,
                item.investor_enterprise_name,item.investorEnterpriseName,
                item.invest_enterprise_name,item.investEnterpriseName
            ].find(value=>String(value == null ? '' : value).trim());
            if (direct) return String(direct).trim();
            if (directList.length) return directList.map(value=>String(value || '').trim()).filter(Boolean).join('、');
            if (!Array.isArray(item.investors)) return '';
            return item.investors.map(investor=>String(
                investor.enterprise_name || investor.enterpriseName || investor.unit_name || investor.unitName
                || investor.user_name || investor.contact_name || ''
            ).trim()).filter(Boolean).filter((name,index,names)=>names.indexOf(name)===index).join('、');
        },
        normalizeActivity(item,index) {
            return {
                ...item,id:String(item.id || 'activity-'+index),type:'activity',title:String(item.name || '').trim(),
                description:this.stripHtml(item.description),cover:this.imageUrl(item.cover_image),
                bannerDate:this.formatBannerDate(item.start_time,item.end_time),time:this.formatTimeRange(item.start_time,item.end_time),
                location:String(item.location || '').trim(),activityForm:String(item.activity_form || '').trim(),
                participants:String(item.participants || '').trim(),fee:this.formatFee(item),
                enterpriseName:String(item.enterprise_name || '').trim(),publisher:String(item.publisher_name || '').trim(),
                followed:Boolean(item.followed),signedUp:Boolean(item.signed_up || item.is_signup)
            };
        },
        normalizeProject(item,index,type) {
            const projectType=type || Object.keys(API_TYPE_LABELS).find(key=>API_TYPE_LABELS[key]===item.type) || 'finance';
            const isOutsourcing=projectType==='crowdfunding';
            const currentQuote=Number(item.current_quote || 0);
            const myQuote=Number(item.my_quote || 0);
            return {
                ...item,id:String(item.id || projectType+'-'+index),type:projectType,title:String(item.name || '').trim(),
                description:this.stripHtml(item.description),cover:this.imageUrl(item.cover_image),
                publishTime:this.formatDate(item.ctime_str || item.start_time),publisher:String(item.publisher_name || '').trim(),
                enterpriseName:String(item.enterprise_name || '').trim(),projectBp:String(item.project_bp || '').trim(),
                followCount:Number(item.follow_count || 0),investorCount:Number(item.investor_count || 0),
                amount:isOutsourcing ? (currentQuote > 0 ? this.formatAmount(currentQuote) : '暂无报价') : this.formatAmount(item.financing_amount),
                actionText:projectType==='finance' ? '投资' : projectType==='crowdfunding' ? '报价' : '查看',
                currentQuote:currentQuote > 0 ? this.formatAmount(currentQuote) : '暂无报价',
                myQuote:myQuote > 0 ? this.formatAmount(myQuote) : '',
                myQuoted:item.my_quoted===true || String(item.my_quoted).toLowerCase()==='true' || Number(item.my_quoted)===1,
                quoteCount:Number(item.quote_count || 0),
                winEnterpriseName:String(item.win_enterprise_name || item.win_enterprise || '').trim(),
                subscriptionUnit:this.normalizeSubscriptionUnit(item),
                followed:Boolean(item.followed),invested:Boolean(item.invested)
            };
        },
        async switchView(view) {
            this.currentView=view;
            this.keyword='';
            await this.fetchCurrentList();
        },
        resetQuery() { this.keyword='';this.fetchCurrentList(); },
        async fetchCurrentList() {
            this.loading=true;
            try {
                if (this.currentView==='activity') {
                    const response=await this.$tools.asyncPost(this,this.$baseUrl.appFinancingActivityList,{ data:{ page:1,limit:100,name:this.keyword || undefined,audit_status:this.auditStatusFilter() } });
                    this.activityList=response && response.data && Number(response.data.code)===0 ? this.unwrap(response).map((item,index)=>this.normalizeActivity(item,index)) : [];
                } else {
                    const response=await this.$tools.asyncPost(this,this.$baseUrl.appFinancingProjectList,{ data:{
                        page:1,
                        limit:100,
                        type:API_TYPE_LABELS[this.currentView],
                        name:this.keyword || undefined,
                        audit_status:this.auditStatusFilter(),
                        ...(this.currentView==='crowdfunding' ? { user_id:this.getUserId() || undefined } : {})
                    } });
                    const next=response && response.data && Number(response.data.code)===0 ? this.unwrap(response).map((item,index)=>this.normalizeProject(item,index,this.currentView)) : [];
                    this.projectList=[...this.projectList.filter(item=>item.type!==this.currentView),...next];
                }
            } catch (error) {
                if (this.currentView==='activity') this.activityList=[];
                else this.projectList=this.projectList.filter(item=>item.type!==this.currentView);
                this.$message.error(error.message || '融资列表获取失败');
            } finally { this.loading=false; }
        },
        openAuditDialog(item) {
            if (!this.isAuditAdmin || !item || !item.id) return;
            this.auditTarget={ ...item,title:item.title || item.name || '--' };
            this.auditForm={ status:Number(item.audit_status)===2 ? 2 : 1,remark:String(item.audit_remark || '').trim() };
            this.auditVisible=true;
            this.$nextTick(()=>{
                if (this.$refs.auditForm) this.$refs.auditForm.clearValidate();
            });
        },
        resetAuditForm() {
            this.auditTarget={};
            this.auditForm={ status:1,remark:'' };
            this.$nextTick(()=>{
                if (this.$refs.auditForm) this.$refs.auditForm.clearValidate();
            });
        },
        submitAudit() {
            if (!this.isAuditAdmin || this.auditSubmitting) return;
            this.$refs.auditForm.validate(async valid=>{
                if (!valid || !this.auditTarget.id) return;
                const user=this.getStoredUser();
                const url=this.auditTarget.type==='activity'
                    ? this.$baseUrl.appFinancingActivityAudit
                    : this.auditTarget.type==='member'
                        ? this.$baseUrl.appFinancingEnterpriseMemberAudit
                        : this.$baseUrl.appFinancingProjectAudit;
                this.auditSubmitting=true;
                try {
                    const response=await this.$tools.asyncPost(this,url,{ data:{
                        id:this.auditTarget.id,
                        status:Number(this.auditForm.status),
                        remark:String(this.auditForm.remark || '').trim(),
                        auditor_id:this.getUserId(),
                        auditor_name:String(user.name || user.real_name || user.user_name || user.uname || '').trim()
                    } });
                    if (response && response.data && Number(response.data.code)===0) {
                        this.auditVisible=false;
                        if (this.auditTarget.type==='member') await this.fetchMembers();
                        else await this.fetchCurrentList();
                        this.$message.success(response.data.msg || '审核成功');
                    } else this.$message.error(response && response.data && response.data.msg || '审核失败');
                } catch (error) { this.$message.error(error.message || '审核失败，请稍后重试'); } finally { this.auditSubmitting=false; }
            });
        },
        openDeleteDialog(item) {
            if (!item || !item.id || (item.type==='member' ? !this.canManageMembers : !this.isAuditAdmin)) return;
            this.deleteTarget={ ...item,title:item.title || item.name || '--' };
            this.deleteVisible=true;
        },
        resetDeleteForm() {
            this.deleteTarget={};
        },
        async submitDelete() {
            if (this.deleteSubmitting || !this.deleteTarget.id || (this.deleteTarget.type==='member' ? !this.canManageMembers : !this.isAuditAdmin)) return;
            const target={ ...this.deleteTarget };
            const url=target.type==='activity' ? this.$baseUrl.appFinancingActivityDel : target.type==='member' ? this.$baseUrl.appFinancingEnterpriseMemberDel : this.$baseUrl.appFinancingProjectDel;
            this.deleteSubmitting=true;
            try {
                const response=await this.$tools.asyncPost(this,url,{ data:{ ids:[target.id] } });
                if (response && response.data && Number(response.data.code)===0) {
                    this.deleteVisible=false;
                    if (target.type==='member') await this.fetchMembers(); else await this.fetchCurrentList();
                    this.$message.success(response.data.msg || '删除成功');
                } else this.$message.error(response && response.data && response.data.msg || '删除失败');
            } catch (error) { this.$message.error(error.message || '删除失败，请稍后重试'); } finally { this.deleteSubmitting=false; }
        },
        async openDetail(item) {
            this.detail={ ...item };
            this.detailVisible=true;
            this.detailLoading=true;
            try {
                const url=item.type==='activity' ? this.$baseUrl.appFinancingActivityGet : this.$baseUrl.appFinancingProjectGet;
                const response=await this.$tools.asyncPost(this,url,{ data:{ id:item.id,user_id:this.getUserId() || undefined } });
                if (response && response.data && Number(response.data.code)===0) {
                    const source=response.data.data || {};
                    this.detail=item.type==='activity' ? this.normalizeActivity({ ...item,...source },0) : this.normalizeProject({ ...item,...source },0,item.type);
                }
            } catch (error) { this.$message.error('详情获取失败，请稍后重试'); } finally { this.detailLoading=false; }
        },
        requireUser() { if (this.getUserId()) return true;this.$message.warning('请先登录');return false; },
        async toggleDetailFollow() {
            if (!this.requireUser() || this.actionLoading) return;
            const activity=this.detail.type==='activity';
            const active=Boolean(this.detail.followed);
            const url=activity
                ? (active ? this.$baseUrl.appFinancingActivityCancelFollow : this.$baseUrl.appFinancingActivityFollow)
                : (active ? this.$baseUrl.appFinancingProjectCancelFollow : this.$baseUrl.appFinancingProjectFollow);
            const payload=activity ? { activity_id:this.detail.id,user_id:this.getUserId() } : { project_id:this.detail.id,user_id:this.getUserId() };
            this.actionLoading=true;
            try {
                const response=await this.$tools.asyncPost(this,url,{ data:payload });
                if (response && response.data && Number(response.data.code)===0) {
                    this.$set(this.detail,'followed',!active);
                    if (!activity) this.$set(this.detail,'followCount',Math.max(0,Number(this.detail.followCount || 0)+(active ? -1 : 1)));
                    this.$message.success(response.data.msg || '操作成功');
                } else this.$message.error(response && response.data && response.data.msg || '操作失败');
            } catch (error) { this.$message.error('操作失败，请稍后重试'); } finally { this.actionLoading=false; }
        },
        async toggleActivitySignup() {
            if (!this.requireUser() || this.actionLoading) return;
            const active=Boolean(this.detail.signedUp);
            const url=active ? this.$baseUrl.appFinancingActivityCancelSignup : this.$baseUrl.appFinancingActivitySignup;
            this.actionLoading=true;
            try {
                const response=await this.$tools.asyncPost(this,url,{ data:{ activity_id:this.detail.id,user_id:this.getUserId() } });
                if (response && response.data && Number(response.data.code)===0) {
                    this.$set(this.detail,'signedUp',!active);
                    this.$message.success(response.data.msg || '操作成功');
                } else this.$message.error(response && response.data && response.data.msg || '操作失败');
            } catch (error) { this.$message.error('操作失败，请稍后重试'); } finally { this.actionLoading=false; }
        },
        handleProjectAction(item) {
            if (!item) return;
            if (item.type==='finance') return this.openInvestDialog(item);
            if (item.type==='crowdfunding') return this.openInvestDialog(item);
            this.openDetail(item);
        },
        openInvestDialog(item) {
            if (!this.requireUser() || this.actionLoading || !item || !item.id) return;
            if (item.type==='crowdfunding') {
                const enterprise=this.getEnterpriseInfo();
                if (!enterprise.id || !enterprise.name) return this.$message.warning('请先完善企业信息');
            }
            const user=this.getStoredUser();
            this.detail={ ...item };
            this.investForm={
                amount:item.type==='crowdfunding' && Number(item.my_quote || 0)>0 ? String(Number(item.my_quote)/10000) : '',
                contactName:String(user.name || user.real_name || user.uname || '').trim(),
                contactPhone:String(user.phone || user.mobile || '').trim()
            };
            this.investVisible=true;
            this.$nextTick(()=>{
                if (this.$refs.investForm) this.$refs.investForm.clearValidate();
            });
        },
        async handleInvest() {
            if (!this.requireUser() || this.actionLoading) return;
            if (this.detail.type==='crowdfunding') {
                return this.openInvestDialog(this.detail);
            }
            if (!this.detail.invested) {
                return this.openInvestDialog(this.detail);
            }
            this.actionLoading=true;
            try {
                const response=await this.$tools.asyncPost(this,this.$baseUrl.appFinancingProjectCancelInvest,{ data:{ project_id:this.detail.id,user_id:this.getUserId() } });
                if (response && response.data && Number(response.data.code)===0) {
                    this.$set(this.detail,'invested',false);
                    this.$message.success(response.data.msg || '已取消投资意向');
                } else this.$message.error(response && response.data && response.data.msg || '取消失败');
            } catch (error) { this.$message.error('取消失败，请稍后重试'); } finally { this.actionLoading=false; }
        },
        resetInvestForm() { this.investForm={ amount:'',contactName:'',contactPhone:'' }; },
        submitInvest() {
            if (this.actionLoading) return;
            this.$refs.investForm.validate(async valid=>{
                if (!valid) return;
                this.actionLoading=true;
                try {
                    const isOutsourcing=this.detail.type==='crowdfunding';
                    const enterprise=this.getEnterpriseInfo();
                    if (isOutsourcing && (!enterprise.id || !enterprise.name)) {
                        this.actionLoading=false;
                        return this.$message.warning('请先完善企业信息');
                    }
                    const inputAmount=Number(this.investForm.amount || 0);
                    const response=isOutsourcing
                        ? await this.$tools.asyncPost(this,this.$baseUrl.appFinancingProjectQuote,{ data:{
                            project_id:this.detail.id,user_id:this.getUserId(),enterprise_id:enterprise.id,
                            enterprise_name:enterprise.name,amount:inputAmount*10000,
                            contact_name:this.investForm.contactName,contact_phone:this.investForm.contactPhone
                        } })
                        : await this.$tools.asyncPost(this,this.$baseUrl.appFinancingProjectInvest,{ data:{
                            project_id:this.detail.id,user_id:this.getUserId(),amount:inputAmount,
                            contact_name:this.investForm.contactName,contact_phone:this.investForm.contactPhone
                        } });
                    if (response && response.data && Number(response.data.code)===0) {
                        if (isOutsourcing) {
                            const quote=inputAmount*10000;
                            const current=Number(this.detail.current_quote || 0);
                            const wasQuoted=Boolean(this.detail.myQuoted);
                            this.$set(this.detail,'myQuoted',true);
                            this.$set(this.detail,'my_quote',quote);
                            this.$set(this.detail,'myQuote',this.formatAmount(quote));
                            this.$set(this.detail,'current_quote',current>0 ? Math.min(current,quote) : quote);
                            this.$set(this.detail,'currentQuote',this.formatAmount(this.detail.current_quote));
                            if (!wasQuoted) this.$set(this.detail,'quoteCount',Number(this.detail.quoteCount || 0)+1);
                        } else {
                            this.$set(this.detail,'invested',true);
                            this.$set(this.detail,'investorCount',Number(this.detail.investorCount || 0)+1);
                        }
                        this.investVisible=false;
                        await this.fetchCurrentList();
                        this.$message.success(response.data.msg || (isOutsourcing ? '报价已提交' : '投资意向已提交'));
                    } else this.$message.error(response && response.data && response.data.msg || (isOutsourcing ? '报价提交失败' : '投资提交失败'));
                } catch (error) { this.$message.error(error.message || '提交失败，请稍后重试'); } finally { this.actionLoading=false; }
            });
        },
        openProjectBp() {
            const url=this.imageUrl(this.detail.projectBp);
            if (url) window.open(url,'_blank');
        },
        openPublish() {
            if (!this.requireUser()) return;
            const enterprise=this.getEnterpriseInfo();
            if (!enterprise.id) return this.$message.warning('企业信息缺失');
            this.publishForm=createPublishForm(this.currentView);
            this.publishEditing=false;
            this.publishEditId='';
            this.publishVisible=true;
        },
        async openEditDialog(item) {
            if (!this.canEditProject(item) || !item.id) return;
            this.publishEditing=true;
            this.publishEditId=String(item.id);
            this.publishForm=this.projectToPublishForm(item);
            this.publishVisible=true;
            this.publishLoading=true;
            try {
                const response=await this.$tools.asyncPost(this,this.$baseUrl.appFinancingProjectGet,{ data:{ id:item.id,user_id:this.getUserId() || undefined } });
                if (response && response.data && Number(response.data.code)===0) {
                    const source=response.data.data || {};
                    this.publishForm=this.projectToPublishForm({ ...item,...source });
                } else if (response && response.data && response.data.msg) {
                    this.$message.warning(response.data.msg);
                }
            } catch (error) {
                this.$message.warning('项目详情获取失败，已使用列表信息');
            } finally { this.publishLoading=false; }
            this.$nextTick(()=>{ if (this.$refs.publishForm) this.$refs.publishForm.clearValidate(); });
        },
        resetPublishForm() {
            if (this.publishForm.coverPreview && /^blob:/i.test(this.publishForm.coverPreview)) URL.revokeObjectURL(this.publishForm.coverPreview);
            this.publishForm=createPublishForm(this.currentView);
            this.publishEditing=false;
            this.publishEditId='';
            this.publishLoading=false;
            this.coverUploading=false;
            this.bpUploading=false;
            this.$nextTick(()=>{ if (this.$refs.publishForm) this.$refs.publishForm.clearValidate(); });
        },
        async uploadFinanceFile(file,folder) {
            const storedToken=String(localStorage.getItem('token') || localStorage.getItem('TOKEN') || '').trim();
            const rawToken=storedToken.replace(/^Bearer\s+/i,'');
            const formData=new FormData();
            formData.append('file',file,file.name || 'file');
            formData.append('rename','0');
            const response=await this.$axios.post(this.$baseUrl.filesupload+'/public/global/'+folder+'?rename=0',formData,{
                headers:{ Token:rawToken ? 'Bearer '+rawToken : '',token:rawToken,'content-type':'multipart/form-data' },timeout:30000
            });
            if (!response || !response.data || Number(response.data.code)!==0) throw new Error(response && response.data && response.data.msg || '文件上传失败');
            const files=Array.isArray(response.data.data) ? response.data.data : [response.data.data];
            const uploaded=files[files.length-1] || {};
            const path=String(uploaded.upload_path || uploaded.file_path || uploaded.url || uploaded.path || uploaded || '').trim();
            if (!path) throw new Error('文件上传失败');
            return path;
        },
        handleCoverChange(file,fileList) {
            const raw=file && (file.raw || file);
            if (!raw) return;
            if (!String(raw.type || '').toLowerCase().startsWith('image/')) {
                if (Array.isArray(fileList)) fileList.splice(fileList.indexOf(file),1);
                return this.$message.error('封面仅支持图片格式');
            }
            if (Number(raw.size || 0)>10*1024*1024) {
                if (Array.isArray(fileList)) fileList.splice(fileList.indexOf(file),1);
                return this.$message.error('封面大小不能超过10MB');
            }
            const sourceUrl=file.url || URL.createObjectURL(raw);
            this.resetCropper();
            this.cropperFile=[{ ...file,raw,url:sourceUrl,__cropperObjectUrl:!file.url }];
            this.cropperKey+=1;
            this.cropperModel.val=true;
        },
        resetCropper() {
            const source=this.cropperFile[this.cropperFile.length-1];
            if (source && source.__cropperObjectUrl && /^blob:/i.test(source.url || '')) URL.revokeObjectURL(source.url);
            this.cropperFile=[];
        },
        dataUrlToFile(dataUrl,name) {
            const parts=String(dataUrl || '').split(',');
            const mimeMatch=(parts[0] || '').match(/:(.*?);/);
            const mime=mimeMatch ? mimeMatch[1] : 'image/jpeg';
            const binary=atob(parts[1] || '');
            const bytes=new Uint8Array(binary.length);
            for (let index=0;index<binary.length;index+=1) bytes[index]=binary.charCodeAt(index);
            return new File([bytes],name,{ type:mime });
        },
        async handleCroppedCover(dataUrl) {
            const oldImage=this.publishForm.coverImage;
            const oldPreview=this.publishForm.coverPreview;
            const file=this.dataUrlToFile(dataUrl,'finance-cover-'+Date.now()+'.jpg');
            this.coverUploading=true;
            this.publishForm.coverPreview=dataUrl;
            try {
                this.publishForm.coverImage=await this.uploadFinanceFile(file,'project');
            } catch (error) {
                this.publishForm.coverImage=oldImage;
                this.publishForm.coverPreview=oldPreview;
                this.$message.error(error.message || '封面上传失败');
            } finally { this.coverUploading=false; }
        },
        async requestBpUpload(request) {
            const file=request.file;
            if (!file) return;
            if (Number(file.size || 0)>30*1024*1024) return this.$message.error('项目 BP 大小不能超过30MB');
            this.bpUploading=true;
            try {
                const path=await this.uploadFinanceFile(file,'project');
                this.publishForm.projectBp=path;
                this.publishForm.bpName=file.name || '项目 BP';
                if (request.onSuccess) request.onSuccess({ path });
            } catch (error) {
                if (request.onError) request.onError(error);
                this.$message.error(error.message || '项目 BP 上传失败');
            } finally { this.bpUploading=false; }
        },
        projectToPublishForm(item) {
            const projectType=Object.keys(API_TYPE_LABELS).find(key=>API_TYPE_LABELS[key]===item.type) || item.type || 'finance';
            const projectBp=String(item.project_bp || item.projectBp || '').trim();
            const bpName=projectBp.split('/').pop() || '';
            return {
                ...createPublishForm(projectType),
                name:String(item.name || item.title || '').trim(),
                description:String(item.description || '').trim(),
                financingAmount:String(item.financing_amount == null ? '' : item.financing_amount).trim(),
                coverImage:String(item.cover_image || item.cover || '').trim(),
                coverPreview:this.imageUrl(item.cover_image || item.cover),
                projectBp,
                bpName
            };
        },
        submitPublish() {
            if (this.publishSaving || this.coverUploading || this.bpUploading) return;
            this.$refs.publishForm.validate(async valid=>{
                if (!valid) return;
                const editing=this.publishEditing;
                const user=this.getStoredUser();
                const enterprise=this.getEnterpriseInfo();
                const activity=this.publishForm.type==='activity';
                const payload=activity ? {
                    name:this.publishForm.name,description:this.publishForm.description,location:this.publishForm.location,
                    activity_form:this.publishForm.activityForm,start_time:this.formatDate(this.publishForm.timeRange[0]),
                    end_time:this.formatDate(this.publishForm.timeRange[1]),fee_type:this.publishForm.feeType,
                    fee_amount:this.publishForm.feeType==='金额' ? Number(this.publishForm.feeAmount || 0) : 0,
                    participants:this.publishForm.participants,cover_image:this.publishForm.coverImage,
                    publisher_id:this.getUserId(),publisher_name:String(user.name || user.real_name || user.uname || '').trim(),
                    enterprise_id:enterprise.id,enterprise_name:enterprise.name
                } : {
                    ...(editing ? { id:this.publishEditId } : {}),
                    type:API_TYPE_LABELS[this.publishForm.type],name:this.publishForm.name,description:this.publishForm.description,
                    financing_amount:this.publishForm.financingAmount,project_bp:this.publishForm.projectBp,cover_image:this.publishForm.coverImage,
                    publisher_id:this.getUserId(),publisher_name:String(user.name || user.real_name || user.uname || '').trim(),
                    enterprise_id:enterprise.id,enterprise_name:enterprise.name
                };
                this.publishSaving=true;
                try {
                    const url=activity ? this.$baseUrl.appFinancingActivityAdd : (editing ? this.$baseUrl.appFinancingProjectEdit : this.$baseUrl.appFinancingProjectAdd);
                    const response=await this.$tools.asyncPost(this,url,{ data:payload });
                    if (response && response.data && Number(response.data.code)===0) {
                        this.currentView=this.publishForm.type;
                        this.publishVisible=false;
                        await this.fetchCurrentList();
                        this.$message.success(response.data.msg || (editing ? '保存成功' : '发布成功'));
                    } else this.$message.error(response && response.data && response.data.msg || (editing ? '保存失败' : '发布失败'));
                } catch (error) { this.$message.error(error.message || (editing ? '保存失败，请稍后重试' : '发布失败，请稍后重试')); } finally { this.publishSaving=false; }
            });
        },
        async openOrganizations() { this.organizationsVisible=true;await this.fetchOrganizations(); },
        async openMembers() { this.membersVisible=true;await this.fetchMembers(); },
        async followedIds(url,key) {
            if (!this.getUserId()) return new Set();
            try {
                const response=await this.$tools.asyncPost(this,url,{ data:{ page:1,limit:1000,user_id:this.getUserId() } });
                return new Set(this.unwrap(response).map(item=>String(item.id || item[key] || '').trim()).filter(Boolean));
            } catch (error) { return new Set(); }
        },
        async fetchOrganizations() {
            this.directoryLoading=true;
            try {
                const [response,followed]=await Promise.all([
                    this.$tools.asyncPost(this,this.$baseUrl.appFinancingOrganizationList,{ data:{ page:1,limit:100,name:this.organizationKeyword || undefined } }),
                    this.followedIds(this.$baseUrl.appFinancingOrganizationUserFollow,'organization_id')
                ]);
                this.organizationList=this.unwrap(response).map((item,index)=>({
                    ...item,id:String(item.id || 'organization-'+index),name:String(item.name || '投资机构').trim(),icon:this.imageUrl(item.icon),
                    type:String(item.type || '').trim(),leader:String(item.leader || '').trim(),investedEnterprises:String(item.invested_enterprises || '').trim(),
                    followed:followed.has(String(item.id || '').trim())
                }));
            } catch (error) { this.organizationList=[];this.$message.error('投资机构获取失败'); } finally { this.directoryLoading=false; }
        },
        async toggleOrganizationFollow(item) {
            if (!this.requireUser()) return;
            const url=item.followed ? this.$baseUrl.appFinancingOrganizationCancelFollow : this.$baseUrl.appFinancingOrganizationFollow;
            try {
                const response=await this.$tools.asyncPost(this,url,{ data:{ organization_id:item.id,user_id:this.getUserId() } });
                if (response && response.data && Number(response.data.code)===0) { this.$set(item,'followed',!item.followed);this.$message.success(response.data.msg || '操作成功'); }
                else this.$message.error(response && response.data && response.data.msg || '操作失败');
            } catch (error) { this.$message.error('操作失败，请稍后重试'); }
        },
        async fetchMembers() {
            this.directoryLoading=true;
            try {
                const [response,followed]=await Promise.all([
                    this.$tools.asyncPost(this,this.$baseUrl.appFinancingEnterpriseMemberList,{ data:{
                        page:1,
                        limit:100,
                        enterprise_name:this.memberKeyword || undefined,
                        ...(this.isAuditAdmin ? {} : { audit_status:1 })
                    } }),
                    this.followedIds(this.$baseUrl.appFinancingEnterpriseMemberUserFollow,'member_id')
                ]);
                this.memberList=this.unwrap(response).map((item,index)=>{
                    const name=String(item.enterprise_name || '企业会员').trim();
                    return {
                        ...item,id:String(item.id || 'member-'+index),type:'member',enterpriseId:String(item.enterprise_id || '').trim(),name,
                        image:this.imageUrl(item.image),financingRound:String(item.financing_round || '').trim(),
                        summary:[item.enterprise_scale,item.application_industry].filter(Boolean).join(' · ') || '暂无企业信息',
                        corporateName:String(item.corporate_name || '').trim(),
                        contactPhone:this.maskPhone(item.contact_phone || item.enterprise_contact_phone || item.enterprise_phone || item.corporate_phone || item.contact_mobile || item.phone || item.mobile || item.tel),
                        followed:followed.has(String(item.id || '').trim())
                    };
                });
            } catch (error) { this.memberList=[];this.$message.error('企业会员获取失败'); } finally { this.directoryLoading=false; }
        },
        async toggleMemberFollow(item) {
            if (!this.requireUser()) return;
            const url=item.followed ? this.$baseUrl.appFinancingEnterpriseMemberCancelFollow : this.$baseUrl.appFinancingEnterpriseMemberFollow;
            try {
                const response=await this.$tools.asyncPost(this,url,{ data:{ member_id:item.id,user_id:this.getUserId() } });
                if (response && response.data && Number(response.data.code)===0) { this.$set(item,'followed',!item.followed);this.$message.success(response.data.msg || '操作成功'); }
                else this.$message.error(response && response.data && response.data.msg || '操作失败');
            } catch (error) { this.$message.error('操作失败，请稍后重试'); }
        },
        maskPhone(value) {
            const phone=String(value || '').trim();
            if (!phone) return '--';
            if (phone.length<7) return phone;
            return phone.slice(0,3)+'****'+phone.slice(-4);
        },
        async applyMember() {
            const enterprise=this.getEnterpriseInfo();
            if (!enterprise.id) return this.$message.warning('企业信息缺失');
            if (this.memberApplying) return;
            this.memberApplying=true;
            try {
                const response=await this.$tools.asyncPost(this,this.$baseUrl.appFinancingEnterpriseMemberAdd,{ data:{ enterprise_id:enterprise.id } });
                if (response && response.data && Number(response.data.code)===0) { await this.fetchMembers();this.$message.success(response.data.msg || '申请提交成功'); }
                else this.$message.error(response && response.data && response.data.msg || '申请提交失败');
            } catch (error) { this.$message.error('申请提交失败，请稍后重试'); } finally { this.memberApplying=false; }
        }
    }
};
</script>

<style lang="scss">
.finance-detail-dialog,
.finance-invest-dialog,
.finance-publish-dialog,
.finance-directory-dialog,
.finance-cropper-dialog {
    max-width: calc(100% - 32px);
}

.finance-detail-dialog .el-dialog__body,
.finance-publish-dialog .el-dialog__body {
    padding: 12px 22px 18px;
}

.finance-directory-dialog .el-dialog__body {
    padding: 12px 20px 20px;
}
</style>

<style scoped lang="scss">
.financing-pc {
    position: relative;
    height: 100%;
    padding: 12px 8px 42px;
    box-sizing: border-box;
    color: #303133;
    background: #f5f5f5;
}

.resou-title {
    height: 48px;
    padding: 0 16px;
    display: flex;
    align-items: center;
    border-left: 4px solid #0079fe;
    box-sizing: border-box;
    background: #fff;
}

.finance-toolbar {
    height: 54px;
    margin-top: 10px;
    padding: 0 18px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    border-bottom: 1px solid #ebeef5;
    background: #fff;
}

.finance-view-tabs {
    height: 100%;
    display: flex;
    align-items: stretch;
}

.finance-view-tabs button {
    position: relative;
    min-width: 105px;
    padding: 0 18px;
    border: 0;
    color: #606266;
    background: transparent;
    cursor: pointer;
}

.finance-view-tabs button.active {
    color: #0079fe;
    font-weight: 600;
}

.finance-view-tabs button.active::after {
    content: '';
    position: absolute;
    right: 18px;
    bottom: 0;
    left: 18px;
    height: 2px;
    background: #0079fe;
}

.finance-links {
    display: flex;
    align-items: center;
}

.finance-link-divider {
    width: 1px;
    height: 16px;
    margin: 0 10px;
    background: #dcdfe6;
}

.finance-query-bar {
    padding: 16px 18px 0;
    background: #fff;
}

.finance-query-bar .el-input {
    width: 240px;
}

.finance-list-wrap {
    height: calc(100% - 184px);
    margin-top: 10px;
    overflow-y: auto;
    background: #fff;
}

.finance-activity-row,
.finance-project-row {
    min-height: 148px;
    padding: 18px 24px;
    display: flex;
    align-items: stretch;
    gap: 22px;
    border-bottom: 1px solid #ebeef5;
    box-sizing: border-box;
    cursor: pointer;
}

.finance-activity-row:hover,
.finance-project-row:hover {
    background: #fafcff;
}

.finance-activity-cover,
.finance-project-cover {
    flex: 0 0 210px;
    height: 118px;
    overflow: hidden;
    border-radius: 4px;
    background: #edf1f7;
}

.finance-activity-cover img,
.finance-project-cover img,
.finance-detail-cover img,
.finance-cover-upload img,
.finance-directory-logo img {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.finance-cover-fallback {
    height: 100%;
    padding: 15px 18px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    box-sizing: border-box;
    color: #fff;
    background: #24427d;
}

.finance-cover-fallback span { font-size: 12px;opacity: .8; }
.finance-cover-fallback strong { margin-top: 8px;font-size: 18px;line-height: 1.35; }
.finance-cover-fallback small { margin-top: 8px;font-size: 11px;opacity: .8; }

.finance-project-cover-fallback {
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #fff;
    font-size: 50px;
    background: #386da6;
}

.finance-row-main {
    flex: 1;
    min-width: 0;
}

.finance-row-title {
    display: flex;
    align-items: center;
    gap: 10px;
}

.finance-row-title h3 {
    min-width: 0;
    margin: 0;
    overflow: hidden;
    color: #303133;
    font-size: 16px;
    font-weight: 600;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.finance-row-description {
    margin: 12px 0;
    overflow: hidden;
    color: #606266;
    line-height: 22px;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.finance-row-meta {
    display: flex;
    flex-wrap: wrap;
    gap: 8px 22px;
    color: #8a93a5;
    font-size: 13px;
}

.finance-subscription-unit {
    margin-top: 8px;
    color: #f56c6c;
    font-size: 14px;
    line-height: 22px;
}

.finance-subscription-unit span {
    white-space: normal;
    word-break: break-all;
}

.finance-project-quotes {
    display: flex;
    flex-wrap: wrap;
    gap: 6px 24px;
    color: #606266;
    font-size: 13px;
    line-height: 22px;
}

.finance-project-quotes span {
    white-space: nowrap;
}

.finance-project-quotes strong {
    color: #f56c6c;
    font-weight: 600;
}

.finance-row-side {
    flex: 0 0 190px;
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    justify-content: center;
    gap: 10px;
}

.finance-row-side strong {
    color: #e54c3c;
    font-size: 20px;
}

.finance-row-side span {
    color: #909399;
    font-size: 12px;
}

.finance-row-actions {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    flex-wrap: wrap;
    gap: 8px;
}

.finance-row-actions .el-button + .el-button {
    margin-left: 0;
}

.finance-audit-status {
    margin-left: 2px;
    font-size: 12px;
    font-weight: 400;
    white-space: nowrap;
}

.finance-audit-pending { color: #e6a23c; }
.finance-audit-approved { color: #67c23a; }
.finance-audit-rejected { color: #f56c6c; }

.finance-delete-confirm {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    color: #606266;
    line-height: 24px;
    word-break: break-all;
}

.finance-delete-confirm i {
    flex: none;
    margin-top: 2px;
    color: #e6a23c;
    font-size: 20px;
}

.cr_footer {
    position: absolute;
    right: 8px;
    bottom: 0;
    left: 8px;
    height: 34px;
    padding: 0 12px;
    display: flex;
    align-items: center;
    box-sizing: border-box;
    color: #0079fe;
    background: #fff;
}

.finance-detail-body {
    max-height: 64vh;
    overflow-y: auto;
}

.finance-detail-cover {
    width: 100%;
    height: 240px;
    overflow: hidden;
    border-radius: 4px;
}

.finance-detail-title-row {
    margin-top: 18px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 15px;
}

.finance-detail-title-row h3 {
    min-width: 0;
    margin: 0;
    font-size: 20px;
    word-break: break-all;
}

.finance-detail-description {
    color: #606266;
    line-height: 24px;
    white-space: pre-wrap;
    word-break: break-all;
}

.finance-detail-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    border-top: 1px solid #ebeef5;
    border-left: 1px solid #ebeef5;
}

.finance-detail-grid > div {
    min-height: 58px;
    padding: 10px 14px;
    display: flex;
    align-items: center;
    gap: 12px;
    border-right: 1px solid #ebeef5;
    border-bottom: 1px solid #ebeef5;
    box-sizing: border-box;
}

.finance-detail-grid span {
    flex: 0 0 72px;
    color: #909399;
}

.finance-detail-grid strong {
    min-width: 0;
    font-weight: 400;
    word-break: break-all;
}

.finance-project-amount {
    margin: 18px 0;
    padding: 18px 20px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    color: #606266;
    background: #f8fafc;
}

.finance-project-amount strong {
    color: #e54c3c;
    font-size: 24px;
}

.finance-quote-value {
    color: #f56c6c;
}

.finance-publish-body {
    max-height: 65vh;
    overflow-y: auto;
    overflow-x: hidden;
    padding-right: 6px;
}

.finance-publish-form {
    width: 100%;
    box-sizing: border-box;
}

.finance-publish-row {
    margin-right: 0 !important;
    margin-left: 0 !important;
}

.finance-publish-row > .el-col {
    box-sizing: border-box;
}

.finance-publish-form .el-form-item {
    margin-bottom: 18px;
}

.finance-publish-form .el-date-editor,
.finance-publish-form .el-select,
.finance-publish-form .el-input,
.finance-publish-form .el-input-number {
    max-width: 100%;
}

.finance-cover-upload {
    width: 250px;
    height: 140px;
    overflow: hidden;
    border: 1px dashed #c0c4cc;
    border-radius: 4px;
    background: #fafafa;
}

.finance-cover-upload > div {
    height: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 8px;
    color: #909399;
}

.finance-cover-upload i { font-size: 30px; }

.finance-directory-toolbar {
    width: 320px;
    margin-bottom: 12px;
}

.finance-directory-name {
    display: flex;
    align-items: center;
    gap: 12px;
}

.finance-directory-logo {
    width: 42px;
    height: 42px;
    flex: 0 0 42px;
    overflow: hidden;
    border-radius: 4px;
    background: #3b78b5;
}

.finance-directory-logo span {
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #fff;
    font-size: 18px;
}

@media (max-width: 1100px) {
    .finance-activity-cover,
    .finance-project-cover { flex-basis: 170px; }
    .finance-row-side { flex-basis: 150px; }
    .finance-view-tabs button { min-width: 90px;padding: 0 10px; }
}

@media (max-width: 760px) {
    .finance-publish-row > .el-col {
        width: 100%;
    }

    .finance-publish-form .el-form-item__label {
        float: none;
        display: block;
        width: auto !important;
        padding: 0 0 6px;
        line-height: 20px;
        text-align: left;
    }

    .finance-publish-form .el-form-item__content {
        margin-left: 0 !important;
    }
}
</style>
